import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../lib/auth.js';
import { api } from '../lib/api.js';
import { supabase } from '../lib/supabase.js';

type Step = 'welcome' | 'connect-tiktok' | 'create-pipeline' | 'run-pipeline' | 'success';

const STEP_ORDER: Step[] = ['welcome', 'connect-tiktok', 'create-pipeline', 'run-pipeline', 'success'];

function storageKey(userId: string) {
  return `cascade_onboarding_step_${userId}`;
}

function getPersistedStep(userId: string): Step {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw && (STEP_ORDER as string[]).includes(raw)) return raw as Step;
  } catch {
    // ignore storage errors
  }
  return 'welcome';
}

function persistStep(userId: string, step: Step) {
  try {
    localStorage.setItem(storageKey(userId), step);
  } catch {
    // ignore storage errors
  }
}

interface PlatformConnection {
  id: string;
  platform: string;
}

interface Pipeline {
  id: string;
  name: string;
}

interface ContentItem {
  id: string;
  status: string;
}

// Step 1: Welcome
function WelcomeStep({ onNext, onSkip }: { onNext: () => void; onSkip: () => void }) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-pix-pink">
        <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-white">Welcome to Cascade</h2>
      <p className="mt-3 max-w-sm text-sm text-white/50">
        Cascade takes your TikTok content and automatically distributes it to YouTube, Instagram, Facebook, and LinkedIn. No manual republishing.
      </p>
      <div className="mt-8 flex flex-col gap-3 w-full max-w-xs">
        <button
          onClick={onNext}
          className="w-full rounded-xl bg-pix-pink px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
        >
          Get started
        </button>
        <button
          onClick={onSkip}
          className="w-full rounded-xl border border-white/10 px-4 py-3 text-sm font-medium text-white/50 transition-colors hover:border-white/15 hover:text-white/70"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}

// Step 2: Connect TikTok
function ConnectTikTokStep({ onNext }: { onNext: () => void }) {
  const [checking, setChecking] = useState(false);
  const [connected, setConnected] = useState(false);

  const checkConnection = useCallback(async () => {
    setChecking(true);
    const result = await api.get<PlatformConnection[]>('/api/platforms');
    const hasTikTok = result.data?.some((c) => c.platform === 'tiktok') ?? false;
    setConnected(hasTikTok);
    setChecking(false);
    return hasTikTok;
  }, []);

  useEffect(() => {
    checkConnection();
    // Check if we're returning from OAuth (URL may have ?connected=tiktok)
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === 'tiktok') {
      checkConnection().then((ok) => { if (ok) onNext(); });
    }
  }, [checkConnection, onNext]);

  function handleConnect() {
    const apiBase = (import.meta.env['VITE_API_URL'] as string) ?? 'http://localhost:3000';
    window.location.href = `${apiBase}/api/platforms/tiktok/connect`;
  }

  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-black">
        <svg viewBox="0 0 24 24" className="h-8 w-8 fill-white" aria-hidden="true">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.79 1.52V6.75a4.85 4.85 0 01-1.02-.06z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-white">Connect TikTok</h2>
      <p className="mt-3 max-w-sm text-sm text-white/50">
        Link your TikTok account so Cascade can pull your videos and turn them into content for other platforms.
      </p>

      {connected && (
        <div className="mt-4 flex items-center gap-2 rounded-lg bg-green-50 px-4 py-2.5 ring-1 ring-green-200">
          <svg className="h-4 w-4 text-green-600" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          <span className="text-sm font-medium text-green-700">TikTok connected</span>
        </div>
      )}

      <div className="mt-8 flex flex-col gap-3 w-full max-w-xs">
        {connected ? (
          <button
            onClick={onNext}
            className="w-full rounded-xl bg-pix-pink px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            Continue
          </button>
        ) : (
          <>
            <button
              onClick={handleConnect}
              className="w-full rounded-xl bg-black px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-80"
            >
              Connect TikTok
            </button>
            <button
              onClick={() => checkConnection()}
              disabled={checking}
              className="w-full rounded-xl border border-white/10 px-4 py-3 text-sm font-medium text-white/50 transition-colors hover:border-white/15 disabled:opacity-50"
            >
              {checking ? 'Checking...' : 'I already connected'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Step 3: Create Pipeline
function CreatePipelineStep({ onNext }: { onNext: (pipelineId: string) => void }) {
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  async function handleCreate() {
    if (!name.trim()) {
      setError('Pipeline name is required.');
      return;
    }
    setCreating(true);
    setError('');
    const result = await api.post<Pipeline>('/api/pipelines', {
      name: name.trim(),
      sourcePlatform: 'tiktok',
      outputPlatforms: ['youtube', 'instagram', 'facebook', 'linkedin'],
      isActive: false,
    });
    setCreating(false);
    if (result.error) {
      setError(result.error.message ?? 'Failed to create pipeline.');
      return;
    }
    if (result.data) {
      onNext(result.data.id);
    }
  }

  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-pix-pink/10">
        <svg className="h-8 w-8 text-pix-pink" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-white">Create your first pipeline</h2>
      <p className="mt-3 max-w-sm text-sm text-white/50">
        A pipeline pulls your TikTok content and distributes it everywhere. Give it a name to get started.
      </p>

      <div className="mt-6 w-full max-w-xs text-left">
        <label htmlFor="pipeline-name" className="block text-sm font-medium text-white/70">
          Pipeline name
        </label>
        <input
          id="pipeline-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
          placeholder="My TikTok content"
          className="mt-1.5 w-full rounded-xl border border-white/10 px-3 py-2.5 text-sm text-white placeholder-gray-400 outline-none ring-0 transition-colors focus:border-pix-pink/50 focus:ring-1 focus:ring-pix-pink/50"
        />
        {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
      </div>

      <div className="mt-6 w-full max-w-xs">
        <button
          onClick={handleCreate}
          disabled={creating}
          className="w-full rounded-xl bg-pix-pink px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {creating ? 'Creating...' : 'Create pipeline'}
        </button>
      </div>
    </div>
  );
}

// Step 4: Run Pipeline
function RunPipelineStep({ pipelineId, onNext }: { pipelineId: string; onNext: () => void }) {
  const [status, setStatus] = useState<'idle' | 'triggering' | 'running' | 'done' | 'error'>('idle');
  const [error, setError] = useState('');

  async function handleRun() {
    setStatus('triggering');
    const result = await api.post<{ triggered: boolean }>(`/api/pipelines/${pipelineId}/run`, {});
    if (result.error) {
      setStatus('error');
      setError(result.error.message ?? 'Failed to trigger pipeline.');
      return;
    }
    setStatus('running');
    // Poll for a content item
    const start = Date.now();
    const maxWait = 60_000; // 60s
    const interval = setInterval(async () => {
      if (Date.now() - start > maxWait) {
        clearInterval(interval);
        // Still advance -- content may arrive later
        setStatus('done');
        onNext();
        return;
      }
      const itemsResult = await api.get<ContentItem[]>(`/api/pipelines/${pipelineId}/content`);
      if (itemsResult.data && itemsResult.data.length > 0) {
        clearInterval(interval);
        setStatus('done');
        onNext();
      }
    }, 3000);
  }

  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-pix-pink/10">
        <svg className="h-8 w-8 text-pix-pink" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-white">Run your pipeline</h2>
      <p className="mt-3 max-w-sm text-sm text-white/50">
        Cascade will pull your latest TikTok content and generate platform-ready copy using AI. This takes about 30 seconds.
      </p>

      {status === 'running' && (
        <div className="mt-6 flex items-center gap-3 rounded-lg bg-indigo-50 px-4 py-3 ring-1 ring-indigo-200">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
          <span className="text-sm text-indigo-700">Processing your content...</span>
        </div>
      )}

      {status === 'error' && (
        <div className="mt-6 rounded-lg bg-red-50 px-4 py-3 ring-1 ring-red-200">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="mt-8 w-full max-w-xs">
        {status === 'idle' || status === 'error' ? (
          <button
            onClick={handleRun}
            className="w-full rounded-xl bg-pix-pink px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            Run pipeline
          </button>
        ) : status === 'running' || status === 'triggering' ? (
          <button
            disabled
            className="w-full rounded-xl bg-pix-pink px-4 py-3 text-sm font-semibold text-white opacity-50"
          >
            Running...
          </button>
        ) : null}
      </div>
    </div>
  );
}

// Step 5: Success
function SuccessStep({ onDone }: { onDone: () => void }) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-green-500">
        <svg className="h-8 w-8 text-white" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-white">You're all set</h2>
      <p className="mt-3 max-w-sm text-sm text-white/50">
        Your first content item is being prepared. Head to the Content Queue to review and publish it.
      </p>
      <div className="mt-8 w-full max-w-xs">
        <button
          onClick={onDone}
          className="w-full rounded-xl bg-pix-pink px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
        >
          Go to Content Queue
        </button>
      </div>
    </div>
  );
}

// Progress dots
function StepDots({ current }: { current: Step }) {
  const steps: Step[] = ['welcome', 'connect-tiktok', 'create-pipeline', 'run-pipeline', 'success'];
  const idx = steps.indexOf(current);
  return (
    <div className="flex items-center justify-center gap-1.5">
      {steps.map((s, i) => (
        <div
          key={s}
          className={[
            'rounded-full transition-all',
            i === idx ? 'h-2 w-6 bg-pix-pink' : 'h-2 w-2 bg-gray-200',
          ].join(' ')}
        />
      ))}
    </div>
  );
}

export default function OnboardingWizard({ onComplete }: { onComplete: () => void }) {
  const { user } = useAuth();
  const [step, setStep] = useState<Step>('welcome');
  const [pipelineId, setPipelineId] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setStep(getPersistedStep(user.id));
    }
  }, [user]);

  function goTo(next: Step) {
    setStep(next);
    if (user) persistStep(user.id, next);
  }

  async function markComplete() {
    if (user) {
      try {
        localStorage.removeItem(storageKey(user.id));
        await supabase.auth.updateUser({ data: { onboarding_completed: true } });
      } catch {
        // Non-fatal
      }
    }
    onComplete();
  }

  async function skip() {
    await markComplete();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-pix-deep p-8 shadow-xl">
        <div className="min-h-[320px] flex flex-col justify-between">
          <div className="flex-1">
            {step === 'welcome' && (
              <WelcomeStep onNext={() => goTo('connect-tiktok')} onSkip={skip} />
            )}
            {step === 'connect-tiktok' && (
              <ConnectTikTokStep onNext={() => goTo('create-pipeline')} />
            )}
            {step === 'create-pipeline' && (
              <CreatePipelineStep
                onNext={(id) => {
                  setPipelineId(id);
                  goTo('run-pipeline');
                }}
              />
            )}
            {step === 'run-pipeline' && pipelineId && (
              <RunPipelineStep pipelineId={pipelineId} onNext={() => goTo('success')} />
            )}
            {step === 'success' && (
              <SuccessStep onDone={markComplete} />
            )}
          </div>

          <div className="mt-8">
            <StepDots current={step} />
          </div>
        </div>
      </div>
    </div>
  );
}
